import { useState } from 'react';
import api from '../../../services/api';
import { queryService } from '../../../services/queryService';
import { useSnackbar } from '../../../components/common/Snackbar';
import { normalizeLifecycleStatus, normalizeId } from '../../../utils/queryListUtils';

/**
 * Hook to manage query operations for RaiseQuery page
 * @param {Function} fetchQueries - Function to refetch queries
 * @returns {Object} Operation handlers and state
 */
export const useRaiseQueryPageOperations = (fetchQueries) => {
  const { showSnackbar } = useSnackbar();
  const [escalatingId, setEscalatingId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [closingId, setClosingId] = useState(null);

  const canEscalate = (query) => {
    if (!query) return false;
    const labelStatus = normalizeLifecycleStatus(query.statusLabel);
    return labelStatus !== 'escalated' && labelStatus !== 'closed';
  };

  const canReopen = (query) => {
    if (!query) return false;
    const normalizedStatus = normalizeLifecycleStatus(query.statusLabel || query.status);
    return normalizedStatus === 'submitted' || normalizedStatus === 'closed';
  };

  const canClose = (query) => {
    if (!query) return false;
    const normalizedStatus = normalizeLifecycleStatus(query.statusLabel || query.status);
    return normalizedStatus !== 'closed';
  };

  const isAssignor = (query) => {
    if (!query) return false;
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const currentUser = JSON.parse(userStr);
      const currentUserId = currentUser.id || currentUser._id;
      const assignorId = query.assignor?._id || query.assignor?.id || query.assignor;
      const raisedById = query.raisedBy?._id || query.raisedBy?.id || query.raisedBy;
      // User is assignor if they assigned it, or if they raised it (and no assignor is set)
      return (
        (assignorId && assignorId.toString() === currentUserId.toString()) ||
        (!assignorId && raisedById && raisedById.toString() === currentUserId.toString())
      );
    } catch (error) {
      // console.error('Failed to check assignor status:', error);
      return false;
    }
  };

  const performManualEscalate = async (query, escalationData = {}) => {
    if (!query?._id || !canEscalate(query)) return;
    setEscalatingId(query._id);
    try {
      const normalizedCurrentStatus = normalizeLifecycleStatus(query.status);
      const normalizedLabelStatus = normalizeLifecycleStatus(query.statusLabel);

      const description =
        (query.statusResponse || '').trim() ||
        'Manually escalated from Raise Query table.';

      // If backend still has status persisted as escalated while the label shows open,
      // reset it first to guarantee a lifecycle transition for notification.
      const escalationTargetCandidates = [
        normalizeId(query.escalatedTo),
        normalizeId(query.assignee),
      ];
      const fallbackTargetId = escalationTargetCandidates.find(Boolean) || null;

      const selectedPath = Array.isArray(escalationData.escalationPath)
        ? escalationData.escalationPath
        : [];
      const primaryTargetId =
        escalationData.primaryTargetId ||
        (selectedPath.length > 0 ? selectedPath[0].userId : null) ||
        fallbackTargetId;

      const escalationSummary =
        selectedPath.length > 0
          ? `Escalation path: ${selectedPath
            .map((item) => `${item.roleLabel || item.role} - ${item.userName}`)
            .join(' -> ')}`
          : null;

      const regionSummary = escalationData.selectedRegion
        ? `Region: ${escalationData.selectedRegion}`
        : null;

      const descriptionParts = [description, regionSummary, escalationSummary].filter(Boolean);
      const finalDescription = descriptionParts.join(' | ');

      if (!primaryTargetId) {
        showSnackbar('No eligible escalation target found. Please assign the query first.', 'error');
        setEscalatingId(null);
        return;
      }

      if (
        normalizedCurrentStatus === 'escalated' &&
        normalizedLabelStatus !== 'escalated'
      ) {
        await queryService.updateQueryStatus(query._id, {
          status: 'raised',
          description: 'Resetting lifecycle before manual escalation',
        });
      }

      await queryService.updateQueryStatus(query._id, {
        status: 'escalated',
        description: finalDescription,
        escalatedToId: primaryTargetId || undefined,
        region: escalationData.selectedRegion || undefined,
      });
      showSnackbar('Query escalated and notifications triggered', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to escalate query';
      showSnackbar(message, 'error');
    } finally {
      setEscalatingId(null);
    }
  };

  const handleEscalateClick = (query) => {
    if (!query) return;
    if (!canEscalate(query)) {
      showSnackbar('This query is already escalated or closed', 'info');
      return;
    }
    return query; // Return query for parent to handle dialog opening
  };

  const handleConfirmEscalation = async (query, escalationData) => {
    if (!query) return;
    try {
      await performManualEscalate(query, escalationData);
    } catch (error) {
      // Error already handled in performManualEscalate
    }
  };

  const handleReopen = async (query) => {
    if (!query?._id || !canReopen(query) || !isAssignor(query)) return;
    
    setReopeningId(query._id);
    try {
      await queryService.updateQueryStatus(query._id, {
        status: 'reopened',
        statusLabel: 'reopened',
        description: 'Query reopened from Raise Query table',
      });
      showSnackbar('Query reopened successfully. Status set to Need Clarification.', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reopen query';
      showSnackbar(message, 'error');
    } finally {
      setReopeningId(null);
    }
  };

  const handleClose = (query) => {
    if (!query) return;
    if (!canClose(query)) {
      showSnackbar('This query is already closed', 'info');
      return;
    }
    return query; // Return query for parent to handle dialog opening
  };

  const handleConfirmClose = async (query) => {
    if (!query?._id || !canClose(query)) return;
    
    setClosingId(query._id);
    try {
      await queryService.updateQueryStatus(query._id, {
        status: 'closed',
        statusLabel: 'closed',
        description: 'Query closed from Raise Query table',
      });
      showSnackbar('Query closed successfully', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to close query';
      showSnackbar(message, 'error');
    } finally {
      setClosingId(null);
    }
  };

  const confirmDelete = async (query) => {
    if (!query) return;
    try {
      await api.delete(`/queries/${query._id}`);
      showSnackbar('Query deleted successfully', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete query';
      showSnackbar(message, 'error');
    }
  };

  return {
    escalatingId,
    reopeningId,
    closingId,
    canEscalate,
    canReopen,
    canClose,
    isAssignor,
    performManualEscalate,
    handleEscalateClick,
    handleConfirmEscalation,
    handleReopen,
    handleClose,
    handleConfirmClose,
    confirmDelete,
  };
};
